/**
 * Decision health — the ONE rule, shared by the app (src/lib/decisionHealth.ts
 * re-exports it) and the public API (GET /v1/decisions?health=). Pure: no
 * imports, so both the Vite app and the Deno edge function load this file.
 *
 * A decision's health comes from:
 *   - whether rationale fields are populated (orphaned if none are)
 *   - whether the review date has passed (stale) or is approaching (aging)
 *
 * Health states:
 *   fresh    — rationale is documented and review date is not approaching
 *   aging    — review date is within 14 days, or rationale is partial
 *   stale    — review date has passed
 *   orphaned — a manual decision with no rationale at all: no rationale fields and nothing written in its body
 */

export type DecisionHealthStatus = 'fresh' | 'aging' | 'stale' | 'orphaned';

export const DECISION_HEALTH_STATUSES: readonly DecisionHealthStatus[] = ['fresh', 'aging', 'stale', 'orphaned'];

/** The fields health reads, in the app's camelCase shape. */
export interface DecisionHealthInput {
  decisionKind?: string | null;
  body?: string | null;
  rationaleContext?: string | null;
  rationaleReasoning?: string | null;
  rationaleAlternatives?: string | null;
  reconsiderationConditions?: string | null;
  reviewByDate?: string | null;
}

const AGING_WINDOW_DAYS = 14;

/**
 * The record form asks for "Context / rationale" in one field and saves it as
 * the decision's body. A decision recorded that way has its rationale: it is
 * not orphaned, and it is not partial.
 */
function hasWrittenBody(d: DecisionHealthInput): boolean {
  return !!d.body?.trim();
}

function hasRationale(d: DecisionHealthInput): boolean {
  return (
    !!(d.rationaleContext || d.rationaleReasoning || d.rationaleAlternatives || d.reconsiderationConditions) ||
    hasWrittenBody(d)
  );
}

function hasFullRationale(d: DecisionHealthInput): boolean {
  return !!(d.rationaleContext && d.rationaleReasoning) || hasWrittenBody(d);
}

export function computeHealthStatus(d: DecisionHealthInput): DecisionHealthStatus {
  const isManual = d.decisionKind === 'manual';

  if (!hasRationale(d)) {
    return isManual ? 'orphaned' : 'fresh';
  }

  if (d.reviewByDate) {
    const reviewDate = new Date(d.reviewByDate);
    const now = new Date();
    const msPerDay = 86_400_000;
    const daysUntilReview = (reviewDate.getTime() - now.getTime()) / msPerDay;

    if (daysUntilReview < 0) return 'stale';
    if (daysUntilReview <= AGING_WINDOW_DAYS) return 'aging';
  }

  if (!hasFullRationale(d)) return 'aging';

  return 'fresh';
}
